import { For, Show, createMemo, createSignal, onCleanup, type Component, type JSX } from 'solid-js';

import { canPlaceAt, dropOrigin, grabOffset, itemAt, rectOf } from '../game/container';
import {
  dropItem,
  lockItem,
  type ContainerPayload,
  type InventoryItem,
} from '../net/protocol';
import type { GameStore } from '../state/store';
import { useDrag } from './DragContext';
import { Icon } from './Icon';
import { FloatingTooltip } from './ItemTooltip';
import { rarityClass } from './stash';
import { affixTint, tintRotation } from './tint';

/**
 * The bag drawn as a Diablo grid: fixed cells, items spanning several of them, drag and
 * drop.
 *
 * **The bag's, and only the bag's.** It briefly drew the vault too, on the reasoning
 * that they were one thing at two sizes — and that reasoning is what the vault stopped
 * being: it is a searchable list of a thousand items now, with no cells to pack, so
 * `VaultList` renders it and this component no longer takes a container id. See
 * `doc/grind/architecture/CONTAINERS-GRID-INVENTORY.md`.
 *
 * ── Why the *cells* are the interactive layer ───────────────────────────────
 *
 * The items are drawn in the same CSS grid, over the cells, with
 * `pointer-events: none`; every pointer event comes from a cell, which knows its own
 * column and row. Nothing here measures an element.
 *
 * That is not a style preference. **jsdom computes no layout**, so a handler that
 * derived a cell from `getBoundingClientRect` would be untestable in the vitest suite
 * and unverifiable except by hand in a browser — for the one interaction in the game
 * where "off by one cell" *is* the bug. Reading the cell off the element that was hit
 * makes the geometry the browser's problem and the meaning testable.
 *
 * Each cell is a **`<button>`**, which matters for more than semantics: a drag needs a
 * mouse, so without a focusable, Enter-able target the grid would have no keyboard path
 * at all — and equipping used to have one, as a list of buttons. `onActivate` is that
 * path.
 */
export interface ItemGridProps {
  store: GameStore;
  container: ContainerPayload;
  /** Prefix for every `data-testid`, so two grids on screen stay distinguishable. */
  testId: string;
  /** Dims an item the caller's filter does not match. Never hides one — see below. */
  dimmed?: (item: InventoryItem) => boolean;
  /** Marks an item the caller considers selected. */
  selected?: (item: InventoryItem) => boolean;
  /** Marks an item that has nowhere to go, without making it unreadable. */
  unwearable?: (item: InventoryItem) => boolean;
  /** Marks an item worth a look — the upgrade caret. */
  badge?: (item: InventoryItem) => boolean;
  /** Click, and Enter on a focused cell. The caller decides what activation means. */
  onActivate?: (item: InventoryItem) => void;
  /** What to show when the pointer rests on an item. */
  tooltip?: (item: InventoryItem) => JSX.Element;
}

/**
 * `CELL_PX` is **gone**. The cell size is `--cell` in `tokens.css`, and the grid, the
 * drag ghost and the scale root all read that one declaration — so the HUD scales at
 * 1440p and 4K without a pixel constant in TypeScript that would have to be scaled in
 * step with it. What the component still sets is the *counts*, which are data.
 */

export const ItemGrid: Component<ItemGridProps> = (props) => {
  const dragging = useDrag();

  /** The item under the pointer, and the cell element to hang its tooltip on. */
  const [hoveredItem, setHoveredItem] = createSignal<InventoryItem | null>(null);

  /**
   * Kept local *and* published to the shared controller, because the two are read in
   * different places: the tooltip below is this grid's, and the paper doll's socket
   * highlight is somewhere a bag cell cannot reach. One setter for both, so a cell can
   * never light a socket for an item whose tooltip is not showing.
   */
  const showItem = (item: InventoryItem | null) => {
    setHoveredItem(item);
    dragging.inspect(item);
  };
  const [anchor, setAnchor] = createSignal<HTMLElement | undefined>();

  /**
   * Each item's own element, by uuid, so a tooltip can hang off **the icon** rather than off
   * whichever grid square the pointer happened to enter.
   *
   * Reported: *when we hover a gear icon in the bag, the mouse hover must be on the icon, not the
   * square grid behind it.* The cells remain the interactive layer — they have to, since they are
   * what know their own column and row, which is what makes the drag geometry the browser's
   * problem — but *anchoring* to them was wrong: a two-by-three chest piece covers six cells, so
   * moving across it re-anchored the card six times and the tooltip walked across the screen
   * while the pointer stayed on one item.
   *
   * Populated by `ref` on each item and cleaned up when the item unmounts, so a sold item cannot
   * leave a detached node behind for the next tooltip to measure against.
   */
  const itemEls = new Map<string, HTMLElement>();

  /** Every cell, row-major — the same order the placement scan uses. */
  const cells = createMemo(() => {
    const out: { col: number; row: number }[] = [];
    for (let row = 0; row < props.container.rows; row++) {
      for (let col = 0; col < props.container.cols; col++) out.push({ col, row });
    }
    return out;
  });

  /**
   * Where the dragged item would land, and whether it would be accepted.
   *
   * Null unless a drag is over *this* grid. The verdict is a **preview** — the server
   * re-checks and wins — so it is allowed to be wrong; what it must not be is absent,
   * because a drag with no highlight is a drag you have to guess at. See
   * `game/container.ts`.
   */
  const pending = createMemo(() => {
    const d = dragging.drag();
    const over = dragging.hovered();
    if (!d || over?.kind !== 'cell') return null;

    const origin = dropOrigin(over, d.grab);
    const size = rectOf(d.item);
    return {
      ...origin,
      width: size.width,
      height: size.height,
      ok: canPlaceAt(props.container, size, origin.col, origin.row, d.item.uuid),
    };
  });

  /** True when a cell is under the pending placement. */
  const inPending = (col: number, row: number) => {
    const p = pending();
    if (!p) return false;
    return col >= p.col && col < p.col + p.width && row >= p.row && row < p.row + p.height;
  };

  const onCellDown = (cell: { col: number; row: number }, event: PointerEvent) => {
    // **Only the primary button picks anything up.** Without this, a right-click began a
    // drag and the context handler below then saw one in flight and cancelled it — so
    // right-click-to-equip did nothing at all on any socket or cell that was not already
    // a special case. A middle-click would have dragged too.
    if (event.button !== 0) return;

    const item = itemAt(props.container, cell.col, cell.row);
    if (!item) return;

    // Alt-click locks, per the lock/favourite system in HUB-ZONES-SCRAP-UI.md. It is
    // handled on the press rather than the click so it never begins a drag — otherwise
    // an alt-click that moved two pixels would both lock the item and pick it up.
    if (event.altKey) {
      props.store.send(lockItem(item.uuid, !item.isLocked));
      return;
    }

    // **Ctrl-click drops it on the floor**, in the same modifier vocabulary and on the
    // press for the same reason. Chosen over drag-to-the-world deliberately: the bag
    // panel covers the canvas, so "release over nothing" is where a *cancelled* drag ends
    // — and making the commonest way to change your mind also the way to throw something
    // away is a misclick that costs an item.
    //
    // The server refuses a padlocked one, which is what makes ctrl a safe modifier to
    // put next to alt: the guard against the misclick is the lock the player already set.
    if (event.ctrlKey || event.metaKey) {
      props.store.send(dropItem(item.uuid));
      return;
    }

    dragging.begin(item, { kind: 'container', container: 'BAG' }, grabOffset(item, cell), {
      x: event.clientX,
      y: event.clientY,
    });
  };

  /**
   * Releasing over a cell.
   *
   * **A release on the cell the drag started from is a click**, not a zero-length move,
   * and reading it the other way was a real fault: it sent a `CMD_MOVE_ITEM` to the cell
   * the item was already in — a wasted command on every click in the bag — and the
   * panel's own click handler fired on top of it, so one click both moved and equipped.
   * `resolveDrop` returns `NO_MOVEMENT` for that case and this is where it becomes an
   * activation.
   */
  const onCellUp = (cell: { col: number; row: number }) => {
    const d = dragging.drag();
    if (!d) return;
    const origin = dropOrigin(cell, d.grab);
    const intent = dragging.drop({ kind: 'cell', col: origin.col, row: origin.row });
    if (intent?.kind === 'nothing' && intent.reason === 'NO_MOVEMENT') {
      props.onActivate?.(d.item);
    }
  };

  // **The grid is always drawn, even empty.** It was briefly replaced by a designed
  // empty state when the container held nothing, and that state had no cells in it — so
  // a player who had equipped their last item could not drag anything *back*, because
  // there was nowhere to drop it. An empty grid is the drop target; the panel puts its
  // empty note beside one rather than instead of it.
  return (
    <>
      <div
        class="grid"
        data-testid={`${props.testId}-grid`}
        /*
          **The hover clears when the pointer leaves the grid, not when it leaves a cell.**

          Reported: *hovering an item in the bag makes the glow in the equipment slots flicker as the
          mouse passes over the grid squares behind it.* An item spans several cells, and the DOM
          fires `leave` on the old cell before `enter` on the new one — so a per-cell clear wrote a
          `null` between every pair. That null reaches the shared drag controller, which is what
          lights the doll sockets an item fits, and the highlight blinked off and on at every cell
          boundary.

          Hoisting it here removes the transient entirely rather than trying to detect it: each cell's
          `enter` sets what is under the pointer — the item, or `null` for an empty square — and the
          only thing that clears is genuinely leaving. Moving *within* one item never writes anything
          but that same item.

          **The cells stay the *event* layer, and the item is what a tooltip anchors to.** The two
          were conflated, which is what the follow-up report was about — *the mouse hover must be on
          the icon, not the square grid behind it*. Cells have to keep the events: the items are drawn
          over them with `pointer-events: none` so every pointer event comes from an element that
          knows its own column and row, which is what makes the drag geometry the browser's problem
          and the meaning testable under jsdom. But *anchoring* to the cell was simply wrong — see
          `itemEls`.
        */
        onPointerLeave={() => showItem(null)}
        style={{
          '--cols': String(props.container.cols),
          '--rows': String(props.container.rows),
        }}
      >
        {/* The interactive layer: one button per cell, each knowing where it is. */}
        <For each={cells()}>
          {(cell) => {
            const item = () => itemAt(props.container, cell.col, cell.row);
            return (
              <button
                type="button"
                class="grid__cell"
                classList={{
                  'grid__cell--ok': inPending(cell.col, cell.row) && pending()?.ok === true,
                  'grid__cell--bad': inPending(cell.col, cell.row) && pending()?.ok === false,
                }}
                data-testid={`${props.testId}-cell-${cell.col}-${cell.row}`}
                data-col={cell.col}
                data-row={cell.row}
                // **Placed explicitly, and this is load-bearing.** The cells used to be
                // auto-flowed, and CSS Grid's auto-placement *avoids areas occupied by
                // explicitly-placed items* — so as soon as the container held anything,
                // every cell was pushed past the items: with three items in a 10 × 4 bag,
                // `data-col="0"` was drawn where column 5 is. Every click and every drag
                // then landed on the wrong cell while the highlight was drawn somewhere
                // else again.
                //
                // With both layers explicit, nothing is auto-placed and the cell grid and
                // the item overlay are the same coordinates by construction.
                style={{
                  'grid-column': String(cell.col + 1),
                  'grid-row': String(cell.row + 1),
                }}
                // The item's name, not the cell's coordinates: a screen reader on an
                // empty square should hear "empty", and on a sword it should hear the
                // sword. An item spanning six cells therefore announces six times, which
                // is the honest reading of six focusable squares holding one thing.
                aria-label={item()?.name ?? 'Empty'}
                onPointerDown={(e) => onCellDown(cell, e)}
                onPointerEnter={(e) => {
                  dragging.hover({ kind: 'cell', ...cell });
                  const under = item() ?? null;
                  showItem(under);
                  // The item's element when there is one, so the card sits by the icon and stays
                  // put while the pointer crosses the rest of it. The cell only anchors an empty
                  // square, which has nothing else to point at.
                  setAnchor((under && itemEls.get(under.uuid)) ?? e.currentTarget);
                }}
                /*
                  **Leaving a cell only clears the hover if the pointer has left the *item*.**

                  Reported: *when hovering an item in the bag, moving across a grid square behind it
                  makes the glow in the equipment slots flicker.* An item spans several cells, so
                  crossing it fires `leave` on one and `enter` on the next — and clearing
                  unconditionally put a `null` between the two. That null reaches the shared drag
                  controller, which is what lights the doll sockets an item fits, so every cell
                  boundary blinked the highlight off and on again.

                  Comparing by `uuid` rather than trusting the event order: pointer enter/leave pairs
                  are not guaranteed to interleave in one direction, and a `null` written after the
                  next cell's `enter` would leave the tooltip stuck closed.

                  **The cells listen; the item is what the card hangs off.** The ask — *hover the
                  icon, not the square grid behind it* — is about where the tooltip belongs, not
                  about which element receives the event, and separating those is what finally
                  satisfied it. The cells must keep the events: they are what know their own column
                  and row, which makes the drag geometry the browser's problem and the meaning
                  testable under jsdom. `setAnchor` resolves the item's own element instead.
                */
                /*
                  **No per-cell leave handler**, and that is the fix. See the grid's own
                  `onPointerLeave` below.
                */
                onFocus={(e) => {
                  const under = item() ?? null;
                  showItem(under);
                  setAnchor((under && itemEls.get(under.uuid)) ?? e.currentTarget);
                }}
                onBlur={() => showItem(null)}
                onPointerUp={() => onCellUp(cell)}
                /**
                 * **Right-click acts on the item** — what that means is the panel's
                 * to decide via `onActivate`: equip it on the character sheet, deposit
                 * it at the vault. The genre's convention (left picks up, right uses),
                 * and the fastest way to move a bagful: no drag, no aim.
                 *
                 * `preventDefault` is not optional here. Without it the browser's own
                 * context menu opens over the panel on every press, which on a game HUD
                 * is both useless and in the way.
                 *
                 * A right-click **during** a drag cancels it instead, because that is
                 * what the same button does in every game that has both gestures — and
                 * because acting on a second item while one is on the cursor would be
                 * ambiguous about which the player meant.
                 */
                onContextMenu={(event) => {
                  event.preventDefault();
                  if (dragging.drag()) {
                    dragging.cancel();
                    return;
                  }
                  const it = item();
                  if (it) props.onActivate?.(it);
                }}
                onClick={(event) => {
                  // **The keyboard path, and only that.** A mouse click already went
                  // through `pointerdown` → `pointerup`, which resolved it — handling the
                  // trailing `click` too would act twice on one gesture, and after an
                  // alt-click it would equip the item it had just locked.
                  //
                  // `detail === 0` is what a click synthesised by Enter or Space on a
                  // button reports; a pointer click reports 1 or more. That is the
                  // discriminator rather than a flag, because a flag has to be cleared
                  // and the case that forgets is a dead button.
                  if (event.detail !== 0) return;
                  const it = item();
                  if (it) props.onActivate?.(it);
                }}
              />
            );
          }}
        </For>

        {/* The items, over the cells and transparent to the pointer. */}
        <For each={props.container.items}>
          {(item) => {
            const at = () => rectOf(item);
            const tint = () => affixTint(item);
            return (
              <div
                ref={(el) => {
                  itemEls.set(item.uuid, el);
                  onCleanup(() => itemEls.delete(item.uuid));
                }}
                class={`grid__item grid__item--${rarityClass(item.rarity)}`}
                classList={{
                  'grid__item--dimmed': props.dimmed?.(item) === true,
                  'grid__item--selected': props.selected?.(item) === true,
                  'grid__item--unwearable': props.unwearable?.(item) === true,
                  'grid__item--locked': item.isLocked,
                  // The dragged item stays in place and goes translucent, rather than
                  // disappearing: the hole it would leave is what the player is aiming
                  // at, and a grid that reflowed mid-drag would move the target.
                  'grid__item--dragging': dragging.drag()?.item.uuid === item.uuid,
                }}
                data-testid={`${props.testId}-item-${item.uuid}`}
                // **`data-wide` when the footprint is wider than it is tall**, which today is
                // the belt and only the belt (`BELT: { w: 2, h: 1 }`; every other footprint is
                // square or upright).
                //
                // Reported: *belts are the only icons in the inventory grid that are overflowing
                // from a square, maybe belts only should be stretched in width.* Every icon is a
                // square 128px painting drawn with `object-fit: contain`, which fits the art to
                // the **short** axis — so in a 2×1 block a belt is scaled to the single cell of
                // height and sits in a square with a cell of dead space beside it, reading as an
                // icon that has come loose from its slot.
                //
                // Keyed off the geometry rather than off `slot === 'BELT'`, so a second wide
                // footprint gets the same treatment without anybody remembering this line.
                data-wide={at().width > at().height ? '' : undefined}
                // **The affix tint**: which family this item's best prefix (or failing that, its
                // best suffix) belongs to, and how far the CSS chain has to rotate to reach it.
                // Absent for an unaffixed item and for every unique — see `ui/tint.ts` for why
                // rarity is deliberately *not* what decides this.
                data-tint={tint()?.family}
                style={{
                  'grid-column': `${at().col + 1} / span ${at().width}`,
                  'grid-row': `${at().row + 1} / span ${at().height}`,
                  ...(tint() ? { '--tint-rotate': `${tintRotation(tint()!.hue)}deg` } : {}),
                }}
              >
                <Icon class="grid__glyph" iconId={item.iconId} glyph={item.glyph} />
                <Show when={props.badge?.(item)}>
                  <span
                    class="grid__badge"
                    data-testid={`upgrade-${item.uuid}`}
                    aria-hidden="true"
                  >
                    ▲
                  </span>
                </Show>
                <Show when={item.isLocked}>
                  <span class="grid__lock" aria-label="locked">
                    ⚿
                  </span>
                </Show>
              </div>
            );
          }}
        </For>
      </div>

      {/* One tooltip for the whole grid rather than one per item: only one can be
          hovered, and a hundred portals that are almost always empty is a hundred
          effects re-running on every frame the container changes. */}
      <Show when={props.tooltip}>
        {(body) => (
          <FloatingTooltip
            anchor={anchor()}
            when={hoveredItem() !== null && dragging.drag() === null}
            testId={`${props.testId}-tip`}
          >
            {/*
              **`keyed`, and it is the whole of a reported bug.**

              Reported: *hovering an item then hovering another item without crossing an empty
              square shows the same tooltip again — the one previously shown.* Solid's `<Show>`
              runs its child function **once**, when the condition becomes truthy, and does not
              re-run it when the value merely changes; `body()(item())` is a call expression rather
              than a JSX prop, so nothing wraps it in a getter and the tooltip is frozen to
              whichever item opened it.

              Crossing an empty cell took `hoveredItem` to `null` and back, which unmounted and
              remounted the child — which is why every existing test passed and why it looked
              intermittent rather than constant.

              `keyed` hands the child the raw value and re-runs it whenever that value changes,
              which is what "one tooltip, whatever is under the pointer" always meant.
            */}
            <Show when={hoveredItem()} keyed>
              {(item) => body()(item)}
            </Show>
          </FloatingTooltip>
        )}
      </Show>
    </>
  );
};
